import { ArrowDown, ArrowUp, Columns3, Loader2, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { apiErrorMessage } from '@/i18n'
import { type Board, type BoardColumn, useBoardColumnMutations } from './api'
import { columnKeyOf } from './board'

// uid ekrandaki satır kimliği: yeni kolonun kodu ad yazıldıkça değişir, satır yeniden çizilmesin.
type Draft = BoardColumn & { uid: string; isNew?: boolean }

/**
 * Panonun kolonları: ad, sıra, "bitti" kolonu ve iş sınırı (WIP). Kayıt kolon kolon yapılır; görev bulunan kolon
 * silinemez, panoda en az bir "bitti" kolonu kalır (sunucu denetler).
 */
export function BoardColumnsModal({ board, counts, onClose }: { board: Board; counts: Record<string, number>; onClose: () => void }) {
  const { t } = useTranslation()
  const { save, remove } = useBoardColumnMutations(board.id)
  const [cols, setCols] = useState<Draft[]>(() => board.columns.map((c) => ({ ...c, uid: c.key })))
  const [busy, setBusy] = useState(false)

  const patch = (i: number, p: Partial<Draft>) => setCols((cs) => cs.map((c, j) => (j === i ? { ...c, ...p } : c)))
  const swap = (i: number, j: number) =>
    setCols((cs) => {
      const a = cs[i]
      const b = cs[j]
      if (!a || !b) return cs
      const out = [...cs]
      out[i] = b
      out[j] = a
      return out
    })
  const add = () =>
    setCols((cs) => {
      const name = t('projects.board.columns.newName')
      return [...cs, { uid: crypto.randomUUID(), key: columnKeyOf(name, cs.map((c) => c.key)), name, position: 0, isDone: false, wipLimit: null, isNew: true }]
    })

  const onRemove = async (i: number) => {
    const c = cols[i]
    if (!c) return
    if (c.isNew) {
      setCols((cs) => cs.filter((_, j) => j !== i))
      return
    }
    if ((counts[c.key] ?? 0) > 0) {
      toast.error(t('projects.board.columns.notEmpty', { name: c.name, count: counts[c.key] }))
      return
    }
    if (!(await confirmModal({ title: t('projects.board.columns.deleteTitle'), description: c.name, confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(c.key, {
      onSuccess: () => setCols((cs) => cs.filter((_, j) => j !== i)),
      onError: (e) => toast.error(apiErrorMessage(e)),
    })
  }

  const onSave = async () => {
    if (cols.some((c) => !c.name.trim())) {
      toast.error(t('projects.board.columns.nameRequired'))
      return
    }
    if (!cols.some((c) => c.isDone)) {
      toast.error(t('projects.board.columns.needDone'))
      return
    }
    setBusy(true)
    try {
      // Önce "bitti" olacaklar yazılır; böylece sunucunun "en az bir bitti kolonu" denetimi ara adımda takılmaz.
      const ordered = cols.map((c, i) => ({ ...c, position: (i + 1) * 10 }))
      for (const c of [...ordered.filter((c) => c.isDone), ...ordered.filter((c) => !c.isDone)]) await save.mutateAsync(c)
      toast.success(t('projects.board.columns.saved'))
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && !busy && onClose()}
      icon={<Columns3 />}
      title={t('projects.board.columns.title', { board: board.name })}
      description={t('projects.board.columns.hint')}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => void onSave()} disabled={busy}>
            {busy ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <div className="hidden items-center gap-2 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:flex">
          <span className="w-6" />
          <span className="flex-1">{t('projects.board.columns.name')}</span>
          <span className="w-24">{t('projects.board.columns.wip')}</span>
          <span className="w-20">{t('projects.board.columns.done')}</span>
          <span className="w-8" />
        </div>
        {cols.map((c, i) => (
          <div key={c.uid} className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2 sm:flex-nowrap">
            <div className="flex w-6 flex-col">
              <Button type="button" variant="ghost" size="icon" className="size-6" disabled={i === 0} aria-label={t('projects.board.columns.up')} onClick={() => swap(i, i - 1)}>
                <ArrowUp />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="size-6" disabled={i === cols.length - 1} aria-label={t('projects.board.columns.down')} onClick={() => swap(i, i + 1)}>
                <ArrowDown />
              </Button>
            </div>
            <div className="min-w-0 flex-1 basis-40">
              <Input
                value={c.name}
                maxLength={120}
                aria-label={t('projects.board.columns.name')}
                onChange={(e) => patch(i, c.isNew ? { name: e.target.value, key: columnKeyOf(e.target.value, cols.filter((_, j) => j !== i).map((x) => x.key)) } : { name: e.target.value })}
              />
              <p className="mt-1 truncate px-1 text-[11px] text-muted-foreground">
                {t('projects.board.columns.taskCount', { count: counts[c.key] ?? 0 })}
              </p>
            </div>
            <Input
              className="w-24"
              inputMode="numeric"
              placeholder={t('projects.board.columns.noLimit')}
              aria-label={t('projects.board.columns.wip')}
              value={c.wipLimit ?? ''}
              onChange={(e) => {
                const n = Number(e.target.value.replace(/\D/g, ''))
                patch(i, { wipLimit: n > 0 ? n : null })
              }}
            />
            <label className="flex w-20 items-center gap-2 text-sm">
              <input type="checkbox" className="size-4 accent-primary" checked={c.isDone} onChange={(e) => patch(i, { isDone: e.target.checked })} />
              {t('projects.board.columns.doneShort')}
            </label>
            <Button type="button" variant="ghost" size="icon" className="ml-auto size-8 text-muted-foreground hover:text-destructive sm:ml-0" aria-label={t('common.delete')} onClick={() => void onRemove(i)}>
              <Trash2 />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus />
          {t('projects.board.columns.add')}
        </Button>
      </div>
    </Modal>
  )
}
